import React, { useState } from 'react'
import { adCreativeService, useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreative, LeadCard } from '@marketing-milk/interfaces'
import { Drawer } from '@mui/material'
import { EmptyLeadCardId } from 'components'
import { Step } from 'components/Stepper/Step'
import { Stepper } from 'components/Stepper/Stepper'
import { equals } from 'lodash/fp'
import { toast } from 'react-toastify'
import { AssignLeadFormsStep } from './AssignLeadFormsStep'
import { DeclareLeadFormsStep } from './DeclareLeadFormsStep'

interface Props {
  isOpen: boolean
  onClose: () => void
  campaignName?: string
  creatives?: AdCreative[]
  onAssigned: () => void
}

const dedupeById = (forms: LeadCard[]): LeadCard[] => {
  const seen = new Set<number>()
  return forms.filter(form => {
    if (seen.has(form.id)) return false
    seen.add(form.id)
    return true
  })
}

export const EditLeadFormsModal = ({
  isOpen,
  onClose,
  campaignName,
  creatives,
  onAssigned,
}: Props) => {
  const businessID = useGetBusinessID()
  const [selectedCreatives, setSelectedCreatives] = useState<AdCreative[]>([])
  // Seeded once from whatever the batch's creatives already carry — the AE shouldn't have to
  // re-declare what's already true. This only needs to run at mount: the modal is remounted
  // fresh each time it's opened for a different draft campaign.
  const [declaredForms, setDeclaredForms] = useState<LeadCard[]>(() =>
    dedupeById(
      (creatives ?? []).map(c => c.leadCardUsed).filter((form): form is LeadCard => Boolean(form))
    )
  )
  const [activeFormId, setActiveFormId] = useState(EmptyLeadCardId)
  const [assigning, setAssigning] = useState(false)
  const [unassigning, setUnassigning] = useState(false)

  const selectCreative = (creative: AdCreative) => {
    const alreadySelected = selectedCreatives.some(c => equals(c, creative))
    setSelectedCreatives(
      alreadySelected
        ? selectedCreatives.filter(c => !equals(c, creative))
        : [...selectedCreatives, creative]
    )
  }

  const selectAll = (visibleCreatives: AdCreative[]) => {
    const selectedIds = new Set(selectedCreatives.map(c => c.id))
    setSelectedCreatives([
      ...selectedCreatives,
      ...visibleCreatives.filter(c => !selectedIds.has(c.id)),
    ])
  }

  const deselectAll = (visibleCreatives: AdCreative[]) => {
    const visibleIds = new Set(visibleCreatives.map(c => c.id))
    setSelectedCreatives(selectedCreatives.filter(c => !visibleIds.has(c.id)))
  }

  const handleClose = () => {
    setSelectedCreatives([])
    onClose()
  }

  // The endpoint applies to every assignable creative and reports the rest by name rather than
  // rejecting the whole batch — one published ad must not block moving the other nineteen. This
  // surfaces exactly which ones didn't land, since the rail's counts alone won't say why.
  const notifyFailures = (failed: Array<{ name: string }>, verb: 'reassigned' | 'unassigned') => {
    if (!failed.length) return
    toast.error(
      `${failed.length} published ad${failed.length === 1 ? '' : 's'} couldn't be ${verb}: ${failed
        .map(f => f.name)
        .join(', ')}`
    )
  }

  // Removing a declared form is global, not scoped to this campaign — it clears the FK on
  // every ad currently carrying it, everywhere. DeclareLeadFormsStep gates this behind a
  // confirmation that names the count before calling it.
  const unassignForm = async (form: LeadCard) => {
    const affected = (creatives ?? []).filter(c => c.leadCardUsed?.id === form.id)
    if (affected.length === 0) {
      onAssigned()
      return
    }
    try {
      const { failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        affected.map(c => c.id),
        EmptyLeadCardId
      )
      notifyFailures(failed, 'unassigned')
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    }
    onAssigned()
  }

  const assignToSelected = async () => {
    if (activeFormId === EmptyLeadCardId || selectedCreatives.length === 0) return

    setAssigning(true)
    try {
      const { updated, failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        selectedCreatives.map(c => c.id),
        activeFormId
      )
      if (updated.length) {
        toast.success(
          `Lead form assigned to ${updated.length} ad${updated.length === 1 ? '' : 's'}`
        )
      }
      notifyFailures(failed, 'reassigned')
      onAssigned()

      // Only the ones that didn't land stay selected, so a retry or the undo button acts on
      // exactly what's still outstanding rather than the whole original selection.
      const failedIds = new Set(failed.map(f => f.id))
      setSelectedCreatives(selectedCreatives.filter(c => failedIds.has(c.id)))
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setAssigning(false)
    }
  }

  // Undo for a bad bulk assignment — select the affected ads and send just those back to
  // Unassigned, rather than the all-or-nothing "remove the whole declared form" in step one.
  const unassignSelected = async () => {
    const toUnassign = selectedCreatives.filter(c => c.leadCardUsed)
    if (toUnassign.length === 0) return

    setUnassigning(true)
    try {
      const { updated, failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        toUnassign.map(c => c.id),
        EmptyLeadCardId
      )
      if (updated.length) {
        toast.success(
          `Lead form removed from ${updated.length} ad${updated.length === 1 ? '' : 's'}`
        )
      }
      notifyFailures(failed, 'unassigned')
      onAssigned()

      const failedIds = new Set(failed.map(f => f.id))
      setSelectedCreatives(selectedCreatives.filter(c => failedIds.has(c.id)))
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setUnassigning(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <div style={{ width: '80vw' }} className="max-w-full">
        <Stepper submitButtonText="Done">
          <Step title="Declare Forms" canContinue={declaredForms.length > 0}>
            <DeclareLeadFormsStep
              businessID={Number(businessID)}
              campaignName={campaignName}
              creatives={creatives ?? []}
              declaredForms={declaredForms}
              setDeclaredForms={setDeclaredForms}
              onUnassignForm={unassignForm}
            />
          </Step>
          <Step title="Assign Ads" onNext={handleClose}>
            <AssignLeadFormsStep
              campaignName={campaignName}
              creatives={creatives}
              declaredForms={declaredForms}
              activeFormId={activeFormId}
              setActiveFormId={setActiveFormId}
              selectedCreatives={selectedCreatives}
              selectCreative={selectCreative}
              selectAll={selectAll}
              deselectAll={deselectAll}
              onSelectLeadless={selectAll}
              onAssign={assignToSelected}
              assigning={assigning}
              onUnassign={unassignSelected}
              unassigning={unassigning}
            />
          </Step>
        </Stepper>
      </div>
    </Drawer>
  )
}
